import React, { useEffect, useRef, useState } from 'react';
import axios, { BASE_URL } from '../utils/axiosInstance';
import './UserSidebar.css';

const isMobile = () =>
  (typeof window !== 'undefined' &&
   window.matchMedia &&
   window.matchMedia('(max-width: 768px)').matches);

const UserSidebar = () => {
  const [user, setUser] = useState(null);
  const [error, setError] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [draftBio, setDraftBio] = useState('');
  const [draftDisplay, setDraftDisplay] = useState(''); // display name
  const [draftFile, setDraftFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  // Stats
  const [stats, setStats] = useState({ friends: null, lists: null });

  const username = localStorage.getItem('username');

  const fetchUser = async () => {
    setError('');
    if (!username) return;
    try {
      const res = await axios.get(`/auth/user/${username}`);
      setUser(res.data);
    } catch (err) {
      console.error('Failed to load user:', err);
      setError('Failed to load user profile.');
    }
  };

  // Stats: best-effort counts for Friends & Lists ranked
  const fetchStats = async () => {
    if (!username) return;
    let friends = null;
    let lists = null;
    try {
      try {
        const r = await axios.get(`/users/${encodeURIComponent(username)}/rankings`);
        const arr = r.data?.lists || r.data || [];
        lists = Array.isArray(arr) ? arr.length : (typeof r.data?.count === 'number' ? r.data.count : null);
      } catch {}
      try {
        const f = await axios.get(`/users/${encodeURIComponent(username)}/friends`);
        const arr = f.data?.friends || f.data || [];
        friends = Array.isArray(arr) ? arr.length : (typeof f.data?.count === 'number' ? f.data.count : null);
      } catch {
        try {
          const f2 = await axios.get('/friends');
          const arr2 = f2.data?.friends || f2.data || [];
          friends = Array.isArray(arr2) ? arr2.length : (typeof f2.data?.count === 'number' ? f2.data.count : null);
        } catch {}
      }
    } finally {
      setStats({ friends, lists });
    }
  };

  useEffect(() => {
    fetchUser();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  if (!username) return null;

  const imgSrc = user?.imageUrl || (user?.image ? `${BASE_URL}${user.image}` : '');
  const handle = `@${String(user?.username || username || '').toLowerCase()}`;
  const displayName = user?.displayName || user?.name || (user?.username || username || '').toUpperCase();

  // Modal controls
  const openEditProfile = () => {
    setDraftBio(user?.bio || '');
    // ✅ prefill from displayName OR name
    setDraftDisplay(user?.displayName || user?.name || '');
    setDraftFile(null);
    setShowModal(true);
  };
  const closeModal = () => {
    if (!saving) {
      setShowModal(false);
      setDraftFile(null);
    }
  };
  const onPickFile = () => fileInputRef.current?.click();
  const onFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    setDraftFile(f);
  };

  const saveProfile = async () => {
    if (!user) return;
    setSaving(true);
    setError('');
    try {
      // avatar
      if (draftFile) {
        const fd = new FormData();
        fd.append('image', draftFile); // field name MUST be "image"
        await axios.patch(`/auth/user/${username}/image`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      // bio
      if ((user.bio || '') !== (draftBio || '')) {
        await axios.patch(`/auth/user/${username}/bio`, { bio: draftBio || '' });
      }
      // display name — try both "displayName" and "name" across a few endpoints
      const currentDisplay = (user.displayName || user.name || '').trim();
      const desiredDisplay = (draftDisplay || '').trim();
      if (currentDisplay !== desiredDisplay) {
        const attempts = [
          // Specific field endpoints
          () => axios.patch(`/auth/user/${username}/displayName`, { displayName: desiredDisplay }),
          () => axios.patch(`/auth/user/${username}/name`,        { name: desiredDisplay }),   // ✅ added

          // Generic user update endpoints
          () => axios.patch(`/auth/user/${username}`, { displayName: desiredDisplay }),
          () => axios.patch(`/auth/user/${username}`, { name: desiredDisplay }),

          // Fallback update route(s)
          () => axios.post('/users/update', { displayName: desiredDisplay }),
          () => axios.post('/users/update', { name: desiredDisplay }),
        ];
        let ok = false;
        for (const fn of attempts) {
          try { await fn(); ok = true; break; } catch {}
        }
        if (!ok) {
          console.warn('Name update attempts failed; check server routes.');
        }
      }
      await fetchUser();
      await fetchStats();
      setShowModal(false);
      setDraftFile(null);
    } catch (err) {
      console.error('Failed to save profile:', err);
      setError('Could not save profile.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <aside className="user-sidebar">
      {/* Card body */}
      <div className="sidebar-inner">
        {error && <div className="error-banner">{error}</div>}

        {user && (
          <>
            {/* === Vertical profile layout (refined) === */}
            <div style={{ display: 'grid', gap: 0, justifyItems: 'center', textAlign: 'center' }}>
              {/* Rounded-corner image */}
              <div
                style={{
                  width: '100%',
                  aspectRatio: '1 / 1',
                  border: '2px solid #0e0e0e',
                  borderRadius: 20,
                  background: '#fff',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                aria-label={`${handle}'s avatar`}
              >
                {imgSrc ? (
                  <img
                    src={imgSrc}
                    alt={`${handle}'s avatar`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }}
                  />
                ) : (
                  <div style={{ fontWeight: 900, fontSize: 28 }}>
                    {(user?.username || username || '?')[0]?.toUpperCase?.() || '?'}
                  </div>
                )}
              </div>

              {/* Display name */}
              <div
                style={{
                  fontWeight: 800,
                  fontSize: 'clamp(1.25rem, 1.05rem + .4vw, 1.5rem)',
                  marginTop: 8,
                  marginBottom: 0
                }}
              >
                {displayName}
              </div>

              {/* @username */}
              <div style={{ color: '#0e0e0e', opacity: 0.8, fontSize: 16, fontWeight: 600, marginTop: 0 }}>
                {handle}
              </div>

              {/* Bio */}
              <div
                style={{
                  marginTop: 20,
                  marginBottom: 20,
                  fontSize: 14,
                  lineHeight: 1.5
                }}
              >
                {user.bio ? user.bio : 'No bio yet.'}
              </div>

              {/* Stats */}
              <div style={{ display: 'flex', gap: 28, marginTop: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontWeight: 800, fontSize: 18, lineHeight: 1 }}>
                    {stats.friends ?? '—'}
                  </span>
                  <span style={{ fontSize: 14, opacity: 0.7, lineHeight: 1 }}>Friends</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontWeight: 800, fontSize: 18, lineHeight: 1 }}>
                    {stats.lists ?? '—'}
                  </span>
                  <span style={{ fontSize: 14, opacity: 0.7, lineHeight: 1 }}>Lists ranked</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="actions" style={{ marginTop: 20, display: 'flex', justifyContent: 'center' }}>
              <button className="btn-primary" onClick={openEditProfile}>
                Edit Profile
              </button>
            </div>
          </>
        )}

        {/* Footer */}
        <footer className="sidebar-footer">
          <p>© 2025 rankr. All Rights Reserved.</p>
        </footer>
      </div>

      {/* Modal overlay */}
      {showModal && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="editProfileTitle">
          <div className="modal">
            <div className="modal-header">
              <h3 className="modal-title" id="editProfileTitle">Edit Profile</h3>
              <button className="modal-close" aria-label="Close" onClick={closeModal}>&times;</button>
            </div>

            <div className="modal-body">
              <div className="modal-stack" style={{ gap: 10 }}>
                {/* Display name input */}
                <label style={{ fontWeight: 700, fontSize: 14 }}>Display name</label>
                <input
                  className="input"
                  value={draftDisplay}
                  onChange={(e) => setDraftDisplay(e.target.value)}
                  maxLength={60}
                  placeholder="e.g., Stefan Hart"
                />

                {/* Image preview */}
                <div className="upload-preview" style={{ marginTop: 8 }}>
                  {draftFile ? (
                    <img src={URL.createObjectURL(draftFile)} alt="New avatar preview" />
                  ) : imgSrc ? (
                    <img src={imgSrc} alt="Current avatar" />
                  ) : (
                    <span>Preview</span>
                  )}
                </div>

                <button type="button" className="upload-btn" onClick={onPickFile}>
                  Choose image…
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={onFileChange}
                  style={{ display: 'none' }}
                />

                <label style={{ fontWeight: 700, fontSize: 14, marginTop: 8 }}>Bio</label>
                <textarea
                  className="modal-textarea"
                  value={draftBio}
                  onChange={(e) => setDraftBio(e.target.value)}
                  rows={5}
                  placeholder="Write a short bio…"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn-cancel btn-hollow-black"
                onClick={closeModal}
                disabled={saving}
                style={{
                  background: '#fff',
                  color: '#0e0e0e',
                  border: '2px solid #0e0e0e',
                  boxShadow: '0 2px 0 #0e0e0e'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-save btn-hollow-black"
                onClick={saveProfile}
                disabled={saving}
                style={{
                  background: '#fff',
                  color: '#0e0e0e',
                  border: '2px solid #0e0e0e',
                  boxShadow: '0 2px 0 #0e0e0e'
                }}
              >
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
};

export default UserSidebar;
